import type { ExperienceIndexCatalogCompetency } from "../queries";
import type { CareerPlanAssessmentAnswer } from "./careerPlanHistory";

export type ExperienceIndexAssessmentOptionView = {
  optionId: string;
  optionContent: string;
  selected: boolean;
};

export type ExperienceIndexAssessmentQuestionView = {
  questionId: string;
  questionContent: string;
  options: ExperienceIndexAssessmentOptionView[];
};

export type ExperienceIndexAssessmentCompetencyView = {
  competencyId: string;
  competencyName: string;
  slug: string | null;
  questions: ExperienceIndexAssessmentQuestionView[];
};

export function groupExperienceIndexAnswers(answers: CareerPlanAssessmentAnswer[]) {
  const groups: CareerPlanAssessmentAnswer[][] = [];
  const indexByCompetencyId = new Map<string, number>();

  for (const answer of answers) {
    const existingIndex = indexByCompetencyId.get(answer.competencyId);
    if (existingIndex == null) {
      indexByCompetencyId.set(answer.competencyId, groups.length);
      groups.push([answer]);
      continue;
    }

    groups[existingIndex].push(answer);
  }

  return groups;
}

export function focusedExperienceIndexAnswerGroups(
  answers: CareerPlanAssessmentAnswer[],
  focusedCompetencyId?: string | null
) {
  const groups = groupExperienceIndexAnswers(answers);
  if (!focusedCompetencyId) {
    return groups;
  }

  return groups.filter((group) => group[0].competencyId === focusedCompetencyId);
}

function selectedOptionView(answer: CareerPlanAssessmentAnswer): ExperienceIndexAssessmentOptionView {
  return {
    optionId: answer.optionId,
    optionContent: answer.optionContent,
    selected: true
  };
}

function selectedOnlyQuestionViews(answers: CareerPlanAssessmentAnswer[]): ExperienceIndexAssessmentQuestionView[] {
  return answers.map((answer) => ({
    questionId: answer.questionId,
    questionContent: answer.questionContent,
    options: [selectedOptionView(answer)]
  }));
}

function catalogQuestionViews(
  catalog: ExperienceIndexCatalogCompetency,
  answers: CareerPlanAssessmentAnswer[]
): ExperienceIndexAssessmentQuestionView[] {
  const savedByQuestionId = new Map(answers.map((answer) => [answer.questionId, answer]));
  const catalogQuestionIds = new Set(catalog.questions.map((question) => question.questionId));

  const questions = catalog.questions.map((question) => {
    const saved = savedByQuestionId.get(question.questionId);
    const options: ExperienceIndexAssessmentOptionView[] = [...question.options]
      .sort((left, right) => right.score - left.score)
      .map((option) => ({
        optionId: option.optionId,
        optionContent: option.optionContent,
        selected: saved?.optionId === option.optionId
      }));

    if (saved && !options.some((option) => option.selected)) {
      options.push(selectedOptionView(saved));
    }

    return {
      questionId: question.questionId,
      questionContent: question.questionContent,
      options
    };
  });

  const orphanedQuestions = selectedOnlyQuestionViews(
    answers.filter((answer) => !catalogQuestionIds.has(answer.questionId))
  );

  return [...questions, ...orphanedQuestions];
}

export function getExperienceIndexAssessmentView(
  competencies: ExperienceIndexCatalogCompetency[],
  answers: CareerPlanAssessmentAnswer[],
  focusedCompetencyId?: string | null
): ExperienceIndexAssessmentCompetencyView[] {
  const catalogById = new Map(competencies.map((competency) => [competency.competencyId, competency]));
  const competencyIds = focusedCompetencyId
    ? [focusedCompetencyId]
    : [...new Set(answers.map((answer) => answer.competencyId))];

  return competencyIds.flatMap((competencyId) => {
    const answersForCompetency = answers.filter((answer) => answer.competencyId === competencyId);
    if (answersForCompetency.length === 0) {
      return [];
    }

    const catalog = catalogById.get(competencyId);
    if (!catalog || catalog.questions.length === 0) {
      return [
        {
          competencyId: answersForCompetency[0].competencyId,
          competencyName: answersForCompetency[0].competencyName,
          slug: catalog?.slug ?? null,
          questions: selectedOnlyQuestionViews(answersForCompetency)
        }
      ];
    }

    return [
      {
        competencyId: catalog.competencyId,
        competencyName: catalog.competencyName,
        slug: catalog.slug,
        questions: catalogQuestionViews(catalog, answersForCompetency)
      }
    ];
  });
}
